import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem, Snackbar, Stack, TextField, TextFieldProps, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TableContext } from '../../contexts/TableContext';
import theme from "../../theme";
import ConfirmDialog from '../modals/ConfirmDialog';

const useStyles = makeStyles(() => ({
    headerIcon: {
        stroke: theme.colors.neutral['500'],
        width: 32,
        height: 32,
    },
    fieldName: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        textTransform: 'capitalize',
    },
    menu: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 400,
        color: 'gray',
    },
    closeIcon: {
        width: '20px',
        height: '20px',
        color: theme.colors.neutral['500'],
        cursor: 'pointer',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            color: theme.colors.neutral['700'],
        }
    },
    deleteIcon: {
        cursor: 'pointer',
        color: theme.colors.neutral['500'],
        transition: 'ease-in-out 300ms',
        '&:hover': {
            color: theme.colors.neutral['700'],
        }
    },
    addTag: {
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
        cursor: 'pointer',
        userSelect: 'none',
        paddingRight: 8,
    },
    input: {
        height: 'fit-content',
        borderRadius: 8,
        border: `1px solid ${theme.colors.neutral['400']}`,
        '&::placeholder': {
            fontSize: '14px',
            lineHeight: '21.7px',
            fontWeight: 400,
            color: theme.colors.neutral['600']
        }
    },
    inputField: {
        minWidth: 'fit-content',
        width: '100%',
        "& .MuiOutlinedInput-root": {
            width: '100%',
            height: 'unset',
            borderRadius: 8,
            padding: '8px !important',
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& > textarea": {
                fontFamily: 'Inter',
                fontSize: '0.95rem',
                padding: 'unset',
            },
            "& fieldset": {
              	border: `2px solid ${theme.colors.neutral['400']} !important`,
            },
            "&:hover fieldset": {
                border: `1px solid ${theme.colors.neutral['600']} !important`,
            },
            "&.Mui-focused fieldset": {
              	border: `2px solid ${theme.palette.primary.main} !important`,
            },
        },
    },
    addStatus: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: 'pointer',
        userSelect: 'none',
        color: theme.colors.primary['500'],
    },
}));

const CustomField: React.FC<{
    fieldName: string,
    fieldContext: string,
    create?: boolean,
    onContext: (context: string) => void,
    onEditTitle?: (fieldName: string) => void,
    onDelete?: () => void,
 }> = ({ fieldName, fieldContext, create, onEditTitle, onContext, onDelete }) => {
    const classes = useStyles();
    const { headers } = useContext(TableContext);

    const [edit, setEdit] = useState<boolean>(false);
    const [editValue, setEditValue] = useState<string>('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);
    const inputRef = useRef<TextFieldProps>(null);
    const contextRef = useRef<TextFieldProps>(null);

    const [snackIsOpen, setSnackIsOpen] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [deletableTag, setDeletableTag] = useState<string>('');

    const isExisting = useMemo(() =>
        !!headers?.find(header => header.trim().toLowerCase() === editValue.trim().toLowerCase())
    , [editValue, headers]);

    useEffect(() => {
        if (create) {
            setEdit(true);
            setEditValue(fieldName);
        }
    // eslint-disable-next-line
    }, [create]);

    return (<>
        <Stack spacing={3} width="100%" height="100%" p={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" pl="8px" pr="12px" width="100%" height="32px">
                {!edit ? (<>
                    <Typography className={classes.fieldName}>{fieldName}</Typography>
                    {(!!onEditTitle && !!onDelete) && (
                        <Box>
                            <IconButton onClick={(event) => {
                                event.stopPropagation();
                                setAnchorEl(event.currentTarget);
                            }}> <Tooltip title="More options">
                                    <MoreVertIcon />
                                </Tooltip>
                            </IconButton>
                            <Menu open={menuOpen} anchorEl={anchorEl}
                                sx={{ '& > .MuiPaper-root': {borderRadius: 3} }}
                                onClose={(e: any) => {
                                    e.stopPropagation();
                                    setAnchorEl(null);
                                }}>
                                <MenuItem className={classes.menu}
                                    onClick={() => {
                                        setAnchorEl(null);
                                        setEditValue(fieldName);
                                        setEdit(true);
                                    }}>
                                    <EditOutlinedIcon fontSize="medium" sx={{ mr: '8px', color: "#7bd4d4" }} />
                                    <Typography fontFamily="Lato" fontWeight="bold" color="black">
                                        {'Rename'}
                                    </Typography>
                                </MenuItem>
                                <MenuItem className={classes.menu} onClick={() => {setAnchorEl(null); onDelete!();}}>
                                    <DeleteIcon fontSize="medium" sx={{ mr: '8px', color: theme.colors.error['500'] }} />
                                    <Typography fontFamily="Lato" fontWeight="bold" color={theme.colors.error['500'] }>
                                        {'Delete'}
                                    </Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}
                </>) : (<>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width={`100%`} maxWidth="100%">
                        <TextField variant="standard" type="text"
                            placeholder={'Enter new text name...'}
                            inputRef={inputRef}
                            defaultValue={fieldName}
                            onChange={(e) => setEditValue(e.target.value)}
                            style={{ width: "100%" }}
                            inputProps={{ style: { fontSize: '1.5rem', color: '#048290', padding: 0, minHeight: "auto" }}}
                            autoFocus fullWidth />
                        <Stack direction="row" spacing={1}>
                            {!isExisting ? (
                                <IconButton onClick={() => !!editValue ? (() => {
                                        onEditTitle?.(inputRef.current?.value as string);
                                        setEdit(false);
                                    })() : undefined} sx={{ cursor: 'pointer' }}
                                    disabled={!editValue}>
                                    <CheckIcon fontSize="small" sx={{
                                        "&.MuiSvgIcon-root": {
                                            fill: '#7bd4d4'
                                        } }} />
                                </IconButton>
                            ) : (
                                <Tooltip placement="top" title={
                                    <Typography component="span" fontSize="0.9rem" fontWeight="600">
                                        {`Column already exists`}
                                    </Typography>
                                    } componentsProps={{tooltip:{sx:{
                                        display: 'flex', alignItems: 'center', backgroundColor: 'rgba(150, 150, 150, 0.9)',
                                        height: '24px', padding: 2,
                                    }}}}>
                                    <IconButton sx={{ cursor: 'not-allowed' }}>
                                        <CheckIcon fontSize="small" sx={{
                                            "&.MuiSvgIcon-root": {
                                                fill: theme.colors.neutral['500']
                                            } }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <IconButton onClick={() => {create ? onDelete!() : setEdit(false);}}>
                                <ClearIcon fontSize="small" sx={{ "&.MuiSvgIcon-root": {fill: 'gray'} }} />
                            </IconButton>
                        </Stack>
                    </Stack>
                </>)}
            </Stack>
            {!create && (
                <TextField variant="outlined"
                    className={classes.inputField}
                    defaultValue={fieldContext}
                    placeholder={'Add more context to what you\'re looking for... (optional)'}
                    onChange={(e) => onContext(e.target.value)}
                    disabled={create}
                    inputRef={contextRef}
                    minRows={3}
                    maxRows={5}
                    sx={{ width: '100%' }}
                    multiline
                />
            )}
        </Stack>
        {snackIsOpen && (
            <Snackbar
                open={snackIsOpen}
                autoHideDuration={2000}
                onClose={() => setSnackIsOpen(false)}
                message={errorMessage}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            />
        )}
        {dialogOpen && (
            <ConfirmDialog
                title="Delete confirmation"
                content={`Are you sure you want to delete this ${deletableTag} field value?`}
                open={dialogOpen}
                confirmCallback={() => {
                    // removeTag(deletableTag);
                    setDeletableTag('');
                    setDialogOpen(false)
                    setErrorMessage('Field value successfully deleted');
                    setSnackIsOpen(true)
                }}
                cancelCallback={() => setDialogOpen(false)}
            />
        )}
    </>);
}

export default CustomField;